/**
 * Shared types/helpers for the Operaciones / ingesta screen.
 *
 * Wire contract: `signUploads` mints one opaque `upload_url` per FileSpec; the
 * browser PUTs the bytes directly to that URL (the API never sees the bytes).
 * `upload_url` is OPAQUE — used verbatim as the PUT target. Each FileSpec
 * DECLARES its document `tipo` explicitly (validated server-side against the
 * validation profile — EXACER here); the server derives the stored object name
 * (`{tipo}{ext}`) itself, so the client never sends filenames.
 */

/** The documents that make up an expediente (DocumentKind enum). The INE back
 * (`ine_reverso`) is OPTIONAL — it carries the MRZ the internal INE check needs. */
export const DOC_KINDS = ["ine", "ine_reverso", "certificado", "foto"] as const;
export type DocKind = (typeof DOC_KINDS)[number];

/** Documents required for submit; the INE back is optional. */
export const REQUIRED_DOC_KINDS: readonly DocKind[] = ["ine", "certificado", "foto"];

export function isRequiredDoc(kind: DocKind): boolean {
  return REQUIRED_DOC_KINDS.includes(kind);
}

export const DOC_KIND_LABEL: Record<DocKind, string> = {
  ine: "INE (frente)",
  ine_reverso: "INE (reverso)",
  certificado: "Certificado",
  foto: "Foto",
};

/** Per-file lifecycle as seen by the operator. */
export type FileStatus = "pending" | "signing" | "uploading" | "done" | "error";

/** One picked document slot inside an expediente uploader card. */
export type DocSlot = {
  kind: DocKind;
  file: File | null;
  status: FileStatus;
  /** 0–100, meaningful while `uploading`. */
  progress: number;
  /** Spanish error message when `status === "error"`. */
  error?: string;
};

export function newSlots(): DocSlot[] {
  return DOC_KINDS.map((kind) => ({
    kind,
    file: null,
    status: "pending" as FileStatus,
    progress: 0,
  }));
}

/** Best-effort MIME for the FileSpec (falls back to a generic binary type). */
export function contentTypeFor(file: File): string {
  return file.type || "application/octet-stream";
}
